import React from 'react';
import { Check, Info, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import SEO from '@/components/SEO';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';
import OrganizationSchema from '@/components/schema/OrganizationSchema';

const Pricing = () => {
  const pricingCategories = [
    {
      category: 'Domestic Services (South West UK)',
      services: [
        {
          name: 'Domestic Property EPC',
          basePrice: '£120',
          details: [
            '£120 for up to 5 rooms',
            '£5 per additional room',
            'Travel surcharge: £25/hour beyond 1 hour from Exeter',
            'Certificate valid for 10 years',
            'Delivered within 48 hours'
          ],
          link: '/services/domestic_epc'
        },
        {
          name: 'Domestic Energy Advice Report + MEES',
          basePrice: '£180',
          details: [
            '£180 for up to 5 rooms',
            '£5 per additional room',
            'Includes full EPC certificate',
            'MEES compliance guidance',
            'Travel surcharge: £25/hour beyond 1 hour from Exeter',
            'Delivered within 5 working days'
          ],
          link: '/services/domestic_advice'
        }
      ]
    },
    {
      category: 'Commercial Services (South West UK)',
      services: [
        {
          name: 'Commercial EPC - Warehouses & Industrial',
          basePrice: '£180',
          details: [
            '£180 for up to 5 rooms/zones',
            '£30 per additional room/zone',
            'Travel surcharge: £25/hour beyond 1 hour from Exeter',
            'Certificate valid for 10 years',
            'Delivered within 5-7 working days'
          ],
          link: '/services/commercial_warehouse'
        },
        {
          name: 'Commercial EPC - Hotels & Offices',
          basePrice: '£180',
          details: [
            '£180 for up to 150m²',
            '£0.80 per additional m²',
            'Travel surcharge: £25/hour beyond 1 hour from Exeter',
            'Certificate valid for 10 years',
            'Delivered within 5-7 working days'
          ],
          link: '/services/commercial_hotel_office'
        },
        {
          name: 'Commercial Energy Advice Report + MEES',
          basePrice: '£300',
          details: [
            '£300 for up to 5 rooms/zones',
            '£30 per additional room/zone',
            'Includes full commercial EPC',
            'MEES compliance guidance',
            'Travel surcharge: £25/hour beyond 1 hour from Exeter',
            'Delivered within 7 working days'
          ],
          link: '/services/commercial_advice'
        }
      ]
    },
    {
      category: 'Nationwide Digital Services',
      services: [
        {
          name: 'SAP Calculations',
          basePrice: '£180',
          details: [
            '£180 for up to 5 rooms',
            '£30 per additional room',
            'Repeat unit: £65',
            'EPC lodgement: £65',
            'Extension SAP: £120',
            'Simplified Part O: £150',
            'Nationwide digital service - no site visit required',
            'Delivered within 3-5 working days'
          ],
          link: '/sap-calculations'
        },
        {
          name: 'U-Value Calculations',
          basePrice: '£65',
          details: [
            '£65 per building element type',
            'Wall, Floor, Roof, Window/Door: £65 each',
            'Multiple element discount available',
            'Nationwide digital service',
            'Delivered within 3-5 working days'
          ],
          link: '/u-value-calculations'
        },
        {
          name: 'Part O Overheating Calculations',
          basePrice: '£150',
          details: [
            'Simplified Part O: £150 per dwelling',
            'Dynamic thermal modelling: Prices on request',
            'Multi-unit discounts available',
            'Nationwide digital service',
            'Delivered within 3-5 working days'
          ],
          link: '/services/part_o'
        },
        {
          name: 'SBEM Calculations',
          basePrice: 'POA',
          details: [
            'Project-specific pricing: £300-£2,000+',
            'Based on building size and complexity',
            'Multi-building discounts available',
            'Nationwide digital service',
            'Delivered within 5-7 working days',
            'Contact us for a tailored quote'
          ],
          link: '/services/sbem'
        },
        {
          name: 'Thermal Bridge Ψ-Value Calculations',
          basePrice: 'POA',
          details: [
            'Typical range: £150-£500 per project',
            'Based on number of junctions',
            'Nationwide digital service',
            'Fast turnaround',
            'Contact us for a quote'
          ],
          link: '/services/thermal_bridge'
        },
        {
          name: 'Dynamic Thermal Modelling',
          basePrice: 'POA',
          details: [
            'Project-specific pricing',
            'Part O compliance assessments',
            'CIBSE TM52/TM59 assessments',
            'Nationwide digital service',
            'Contact us for a quote'
          ],
          link: '/services/dynamic_thermal'
        },
        {
          name: 'Retrofit Coordination (PAS 2035)',
          basePrice: 'POA',
          details: [
            'Project-specific pricing',
            'Whole-house assessments',
            'Devon & South West coverage',
            'Contact us for a quote'
          ],
          link: '/services/retrofit'
        }
      ]
    },
    {
      category: 'Floor Plans (Exeter & South West)',
      services: [
        {
          name: 'Marketing Floor Plans',
          basePrice: '£85',
          details: [
            '£85 for up to 5 rooms',
            '£5 per additional room',
            'Elevations: £65 per elevation (up to 10)',
            'Sections: £65 per section (up to 5)',
            '3D upgrade: Price on request',
            'DIY option via HousePlanDirect.com from £30'
          ],
          link: '/services/marketing_plans'
        },
        {
          name: 'Leasehold / To-Scale Plans',
          basePrice: '£120',
          details: [
            '£120 for up to 5 rooms',
            '£5 per additional room',
            'Elevations: £65 per elevation (up to 10)',
            'Sections: £65 per section (up to 5)',
            'Suitable for legal documentation',
            'DIY option via HousePlanDirect.com from £30'
          ],
          link: '/services/leasehold_plans'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <SEO 
        title="Pricing | EPC & Energy Assessment Costs | Energy Performance Direct"
        description="Transparent pricing for EPCs, energy assessments, SAP calculations, U-values and more. Domestic EPC from £120, Commercial EPC from £180, SAP from £180. No VAT to pay — we are not VAT registered. South West UK and nationwide services."
        keywords="EPC cost, EPC price Exeter, SAP calculation price, commercial EPC cost, energy assessment pricing, U-value calculation cost"
        canonical="/pricing"
      />
      <OrganizationSchema />
      <Breadcrumbs items={[
        { name: 'Pricing', path: '/pricing' }
      ]} />
      <Navbar />

      {/* Header */}
      <section className="text-white py-16" style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4" data-testid="pricing-heading">
              Transparent Pricing
            </h1>
            <p className="text-base sm:text-lg opacity-95">
              Clear, honest pricing for all our energy assessment and building compliance services
            </p>
          </div>
        </div>
      </section>

      {/* Key Information */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Card className="border-none shadow-lg">
                <CardContent className="p-6 text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-3">
                    <Check className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">No VAT to pay</h3>
                  <p className="text-sm text-gray-600">All prices shown are final — we are not VAT registered, so no VAT is added at checkout.</p>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg">
                <CardContent className="p-6 text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-amber-100 rounded-full mb-3">
                    <Info className="w-6 h-6 text-amber-600" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">Travel Surcharge</h3>
                  <p className="text-sm text-gray-600">£25/hour for properties >1 hour from Exeter</p>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg">
                <CardContent className="p-6 text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-cyan-100 rounded-full mb-3">
                    <Info className="w-6 h-6 text-cyan-600" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">Large Properties</h3>
                  <p className="text-sm text-gray-600">Properties over 25 rooms or multiple properties: Contact for quote</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Tables */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto space-y-12">
            {pricingCategories.map((category, catIndex) => (
              <div key={catIndex}>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">{category.category}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {category.services.map((service, serviceIndex) => (
                    <Card key={serviceIndex} className="border-none shadow-lg hover:shadow-xl transition-all" data-testid={`pricing-card-${catIndex}-${serviceIndex}`}>
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <h3 className="text-xl font-bold text-gray-900 flex-1">{service.name}</h3>
                          <span className="text-2xl font-bold text-green-600 whitespace-nowrap ml-4">{service.basePrice}</span>
                        </div>
                        <ul className="space-y-2 mb-6">
                          {service.details.map((detail, detailIndex) => (
                            <li key={detailIndex} className="flex items-start gap-2 text-sm text-gray-700">
                              <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                              <span>{detail}</span>
                            </li>
                          ))}
                        </ul>
                        <a href={service.link}>
                          <Button 
                            className="w-full text-white"
                            style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}
                            data-testid={`view-service-${catIndex}-${serviceIndex}`}
                          >
                            View Service Details
                            <ArrowRight className="ml-2 w-4 h-4" />
                          </Button>
                        </a>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 text-white" style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Book Your Service?</h2>
            <p className="text-lg mb-8 opacity-95">
              Get started with transparent pricing and professional service
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => window.location.href = '/booking'}
                size="lg"
                className="bg-white text-green-600 hover:bg-gray-100 px-12 py-6 text-lg rounded-full shadow-lg"
                data-testid="pricing-book-btn"
              >
                Book Now
              </Button>
              <Button 
                onClick={() => window.location.href = '/contact'}
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-green-600 px-12 py-6 text-lg rounded-full"
                data-testid="pricing-contact-btn"
              >
                Get a Quote
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Pricing;
